import { Circle } from 'lucide-react';

import { Chip, cn, Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui';
import { ApiRegion } from '@/config';
import { useRegionalApis } from '@/context/RegionScope';
import { useSocketIOPresenceIsOnline } from '@/socketio/SocketIOService';

export const PatientID = (props: {
  userId: string | number | null | undefined;
  hideOffline?: boolean;
  isPriority: boolean | null | undefined;
}) => {
  const { userId, isPriority } = props;
  const { region } = useRegionalApis();
  // The presence socket only tracks Canada patients; a US id with the same number is not the same patient.
  const isCanada = region === ApiRegion.CA;
  const { isOnline: presenceOnline, isConnected } = useSocketIOPresenceIsOnline(userId);

  if (!userId) {
    return <span />;
  }

  const isOnline = isCanada && presenceOnline;

  if (!isCanada || !isConnected) {
    return (
      <span title={isPriority ? '(Priority User)' : ''}>
        {userId}
        {isPriority && (
          <Chip className="mx-0.5" size="small" tone="critical" variant="solid" title="Priority patient">
            P
          </Chip>
        )}
      </span>
    );
  }

  if (props.hideOffline && !isOnline) {
    return (
      <span title={isPriority ? '(Priority User)' : ''}>
        {userId}{' '}
        {isPriority && (
          <Chip className="mx-0.5" size="small" tone="critical" variant="solid" title="Priority patient">
            P
          </Chip>
        )}
      </span>
    );
  }

  return (
    <span>
      {userId}
      {isPriority && (
        <Chip className="mx-0.5" size="small" tone="critical" variant="solid" title="Priority patient">
          P
        </Chip>
      )}
      <div
        className={cn('ml-0.5 inline-block size-3 rounded-full', isOnline ? 'bg-positive' : 'bg-ink-subtle')}
        title={(isOnline ? 'Online' : 'Offline') + (isPriority ? ' (Priority User)' : '')}
      />
    </span>
  );
};

export const PatientIDCondensed = (props: {
  userId: string | number | null | undefined;
  hideOffline?: boolean;
  isPriority: boolean | null | undefined;
}) => {
  const { userId, isPriority } = props;
  const { region } = useRegionalApis();
  // The presence socket only tracks Canada patients; a US id with the same number is not the same patient.
  const isCanada = region === ApiRegion.CA;
  const { isOnline: presenceOnline } = useSocketIOPresenceIsOnline(userId);

  if (!userId) {
    return <span />;
  }

  const isOnline = isCanada && presenceOnline;

  return (
    <span className="inline-flex items-center gap-2">
      <span>P#{userId}</span>
      {isPriority && (
        <Tooltip>
          <TooltipTrigger asChild>
            <strong className="text-critical">P</strong>
          </TooltipTrigger>
          <TooltipContent>Priority patient</TooltipContent>
        </Tooltip>
      )}
      {isCanada && !props.hideOffline && (
        <Tooltip>
          <TooltipTrigger asChild>
            <Circle
              aria-label={isOnline ? 'Online' : 'Offline'}
              className={cn('size-3 fill-current', isOnline ? 'text-positive' : 'text-ink-subtle')}
            />
          </TooltipTrigger>
          <TooltipContent>Patient is {isOnline ? 'online' : 'offline'}</TooltipContent>
        </Tooltip>
      )}
    </span>
  );
};
